import type { Announcement } from "../services/AnnouncementService";
import { stripEmoji } from "./alertFeed";

// Text helpers shared by the announcement popup and the bell inbox.

export type Lang = "en" | "es";

export const localized = (a: Announcement, lang: Lang) => ({
  title: lang === "es" && a.titleEs ? a.titleEs : a.title,
  body: lang === "es" && a.bodyEs ? a.bodyEs : a.body,
});

// Social-style posts ("WALMART SHOPPERS! ...") often repeat the headline as the body's first line.
export const withoutRepeatedTitle = (body: string, title: string) => {
  const head = stripEmoji(title).toLowerCase();
  const trimmed = body.trimStart();
  return head && trimmed.toLowerCase().startsWith(head) ? trimmed.slice(head.length).replace(/^[\s!.:-]+/, "") : body;
};

// Posts that mention a price but don't follow the "N% off ... Now $X (was $Y)" format.
export const mentionsPrice = (a: Announcement) => /\$\d|price drop|clearance/i.test(`${a.title} ${a.body}`);

// "LOWE'S CRAFTSMAN MULTI-TOOL!" reads as shouting; show it as "Lowe's Craftsman Multi-Tool!".
export const calmCase = (text: string) => {
  const letters = text.replace(/[^A-Za-z]/g, "");
  if (letters.length < 6 || letters !== letters.toUpperCase()) return text;
  return text.toLowerCase().replace(/(^|[\s\-/(])([a-z])/g, (_, sep: string, ch: string) => sep + ch.toUpperCase());
};

export const headlineOf = (text: string) => calmCase(stripEmoji(text));

export function formatRelativeTime(timestamp: number, lang: Lang = "en"): string {
  const diff = Date.now() - timestamp;
  const mins = Math.max(Math.floor(diff / 60000), 1);
  if (mins < 60) return lang === "es" ? `hace ${mins}m` : `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return lang === "es" ? `hace ${hrs}h` : `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return lang === "es" ? `hace ${days}d` : `${days}d ago`;
  return new Date(timestamp).toLocaleDateString(lang === "es" ? "es-ES" : "en-US", { month: "short", day: "numeric" });
}

export const percentLabel = (pct: number, lang: Lang) => (lang === "es" ? `${pct}% dto.` : `${pct}% off`);
